/** Thin client for the Worker API (live mode). */
import type { Connection, ResolverRow, StartResponse, Verdict } from "./types";

export async function startTest(): Promise<StartResponse> {
  const res = await fetch("/api/start", { method: "POST" });
  if (!res.ok) throw new Error(`start failed: ${res.status}`);
  return (await res.json()) as StartResponse;
}

export async function fetchConnection(): Promise<Connection> {
  const res = await fetch("/api/connection");
  if (!res.ok) throw new Error(`connection failed: ${res.status}`);
  const c = (await res.json()) as Connection & { flag?: string };
  return { ...c, flag: c.flag ?? flagFor(c.cc) };
}

export interface StreamHandlers {
  onResolver: (row: ResolverRow) => void;
  onNs: (counts: Record<string, number>) => void;
  onDone: (verdict: Verdict, resolvers: ResolverRow[], nsCounts?: Record<string, number>) => void;
}

/** Open the SSE stream for a test. Returns a closer. Falls back to polling if
 *  EventSource is unavailable. */
export function openStream(testId: string, h: StreamHandlers): () => void {
  if (typeof EventSource !== "undefined") {
    const es = new EventSource(`/api/test/${testId}/stream`);
    es.addEventListener("resolver", (e) => h.onResolver(JSON.parse((e as MessageEvent).data)));
    es.addEventListener("ns", (e) => h.onNs(JSON.parse((e as MessageEvent).data)));
    es.addEventListener("done", (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { verdict: Verdict; resolvers: ResolverRow[]; nsCounts?: Record<string, number> };
      h.onDone(d.verdict, d.resolvers, d.nsCounts);
      es.close();
    });
    return () => es.close();
  }
  return pollSnapshot(testId, h);
}

function pollSnapshot(testId: string, h: StreamHandlers): () => void {
  const seen = new Set<string>();
  let stop = false;
  const tick = async () => {
    if (stop) return;
    try {
      const res = await fetch(`/api/test/${testId}`);
      const d = (await res.json()) as { verdict: Verdict; resolvers: ResolverRow[]; nsCounts?: Record<string, number> };
      for (const r of d.resolvers) if (!seen.has(r.ip)) { seen.add(r.ip); h.onResolver(r); }
      if (d.nsCounts) h.onNs(d.nsCounts);
    } catch { /* transient */ }
    if (!stop) setTimeout(tick, 500);
  };
  tick();
  return () => { stop = true; };
}

export function flagFor(cc: string): string {
  if (!/^[A-Za-z]{2}$/.test(cc)) return "🏳️";
  const base = 0x1f1e6;
  const up = cc.toUpperCase();
  return String.fromCodePoint(base + (up.charCodeAt(0) - 65), base + (up.charCodeAt(1) - 65));
}
